// "Download for Mac", "Download for Windows" and "Download for Android", then how to install it (spec "Getting ready",
// "Get the apps"): once the download starts, its three steps appear under the button. Mac: open the disk image, drag
// Cloud Machine to Applications, open it from there. Windows: open the installer, install (for this person, no admin),
// open it from Start. Android: open the download, let the browser install apps if Android asks, open it. The same on
// Getting ready, the dashboard's apps and the guide; each page gives its own button look.

import { useState, type ReactNode } from 'react';
import { IconCommand } from '@tabler/icons-react';
import './app-download.css';

export type AppPlatform = 'mac' | 'windows' | 'android';

const icon = (d: ReactNode) => <svg viewBox="0 0 24 24" aria-hidden="true">{d}</svg>;
const downloadIcon = icon(<path d="M12 4v11m0 0-4.5-4.5M12 15l4.5-4.5M5 19.5h14" />);

/** Each app's download: where it's served, the name it saves as, and the steps after. */
export const APPS: Record<AppPlatform, { href: string; file: string; label: string; steps: { icon: ReactNode; title: string; detail: ReactNode }[] }> = {
  mac: {
    href: '/dl/cmx-mac.dmg',
    file: 'Cloud Machine.dmg',
    label: 'Download for Mac',
    steps: [
      {
        icon: downloadIcon,
        title: 'Open the download',
        detail: (
          <>
            <b>Cloud Machine.dmg</b>, in your Downloads
          </>
        ),
      },
      {
        icon: icon(
          <>
            <rect x="2.5" y="7" width="7" height="7" rx="2" />
            <path d="M12 10.5h4.5m0 0-2-2m2 2-2 2" />
            <path d="M18.5 6.5h1.6l.9 1H22v8.5h-6V7.5" />
          </>,
        ),
        title: 'Drag it to Applications',
        detail: 'In the window that opens',
      },
      {
        icon: icon(
          <>
            <circle cx="10.5" cy="10.5" r="5.5" />
            <path d="m15 15 4.5 4.5" />
          </>,
        ),
        title: 'Open Cloud Machine',
        detail: (
          <>
            From Applications, or{' '}
            <span className="app-install-keys">
              <kbd>
                <IconCommand className="kbd-icon" size={12} stroke={1.75} aria-label="Command" />
              </kbd>{' '}
              <kbd>Space</kbd>
            </span>{' '}
            and type its name
          </>
        ),
      },
    ],
  },
  windows: {
    href: '/dl/cmx-windows.exe',
    file: 'Cloud Machine Setup.exe',
    label: 'Download for Windows',
    steps: [
      {
        icon: downloadIcon,
        title: 'Open the download',
        detail: (
          <>
            <b>Cloud Machine Setup.exe</b>, in your Downloads. If Windows protects your PC, choose <b>More info</b>, then <b>Run anyway</b>
          </>
        ),
      },
      {
        icon: icon(
          <>
            <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
            <path d="M3.5 8.5h17" />
            <path d="m9 14 2 2 4-4.5" />
          </>,
        ),
        title: 'Install it',
        detail: 'Just for you, so no admin is needed',
      },
      {
        icon: icon(
          <>
            <rect x="4" y="4" width="6.5" height="6.5" rx="1.5" />
            <rect x="13.5" y="4" width="6.5" height="6.5" rx="1.5" />
            <rect x="4" y="13.5" width="6.5" height="6.5" rx="1.5" />
            <rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.5" />
          </>,
        ),
        title: 'Open Cloud Machine',
        detail: 'It opens when it’s installed, and it’s in Start after that',
      },
    ],
  },
  android: {
    href: '/dl/cmx-android.apk',
    file: 'Cloud Machine.apk',
    label: 'Download for Android',
    steps: [
      {
        icon: downloadIcon,
        title: 'Open the download',
        detail: (
          <>
            <b>Cloud Machine.apk</b>, from the browser’s downloads. If the browser asks, choose <b>Download anyway</b>
          </>
        ),
      },
      {
        icon: icon(
          <>
            <rect x="3" y="8" width="18" height="8" rx="4" />
            <circle cx="17" cy="12" r="2" />
          </>,
        ),
        title: 'Allow installing from this browser',
        detail: (
          <>
            If Android asks: Settings opens; turn on <b>Allow from this source</b>, go back, then <b>Install</b>
          </>
        ),
      },
      {
        icon: icon(
          <>
            <circle cx="7" cy="7" r="2.5" />
            <circle cx="17" cy="7" r="2.5" />
            <circle cx="7" cy="17" r="2.5" />
            <circle cx="17" cy="17" r="2.5" />
          </>,
        ),
        title: 'Open Cloud Machine',
        detail: 'From the installer, and from your apps after that',
      },
    ],
  },
};

/** The download button, and its steps once it's clicked (or from the start: `steps`, where the page is about installing). */
export function AppDownload({ platform, className, children, steps = false }: { platform: AppPlatform; className: string; children?: ReactNode; steps?: boolean }) {
  const [clicked, setClicked] = useState(false);
  const started = steps || clicked;
  const a = APPS[platform];
  return (
    <div className="app-download">
      <a className={className} href={a.href} download={a.file} onClick={() => setClicked(true)} data-testid={`start-download-${platform}`}>
        {children ?? a.label}
      </a>
      {started && (
        <ol className="app-install" aria-label="Installing Cloud Machine" data-testid={`${platform}-install-steps`}>
          {a.steps.map((s, i) => (
            <li key={s.title} style={{ ['--i' as string]: i }}>
              <span className="app-install-icon">{s.icon}</span>
              <span className="app-install-text">
                <b>
                  {i + 1}. {s.title}
                </b>
                <span>{s.detail}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
